// Who is at the tablet, and what the device remembers between them.
//
// # Three states, not two
//
//   - **Unpaired.** Nobody has ever signed in here. The only thing on screen is
//     the parent's sign-in.
//   - **Paired, nobody signed in.** The device knows its family and the names
//     of the children in it, and shows the profile picker. This is where the
//     app spends most of its life: a child walks up, taps their name, types
//     four digits, and works. No parent login, every afternoon — the roster is
//     not a credential, and the PIN is checked by the server.
//   - **Someone signed in.** A child holding a six-hour token, or a parent who
//     has entered parent mode.
//
// The pairing is not a credential. It is the family id and the child names,
// which are not secret — the PIN is what is secret, and it is checked by the
// server. Keeping the roster locally is what makes the daily flow one tap.
//
// # Parent mode
//
// Parent mode needs both: a parent credential in *this tab*, and the parent
// PIN, which the server checks against the `families` row. The PIN
// re-authenticates a parent who is already signed in rather than creating a
// session (the port plan's *Auth* section) — a four-digit password on a device
// children use would be a bad password. The credential is per-tab, so a parent
// who closed the tab signs in again; the child flow is untouched by that,
// because it never needed a parent credential in the first place.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { AUTH_MODE, configureClient, unwrap, type Credential } from '../api/client';
import {
  childSignIn,
  claimReferral,
  createOrganization,
  listChildren,
  login,
  logout,
  me,
  refreshSession,
} from '../api/gen';
import type { Child, Membership } from '../api/gen';
import { clearReferral, pendingReferral } from './share';

const PAIRING_KEY = 'sokrates.pairing';
const PARENT_KEY = 'sokrates.parent';
const CHILD_KEY = 'sokrates.child';

/** What the device remembers about its family between sign-ins. */
export interface Pairing {
  orgId: string;
  orgName: string;
  children: Child[];
}

/** The parent's own credential, kept so parent mode can re-authenticate. */
interface Parent {
  /** An access token — this app's own or Zitadel's — or a test subject
   * under the header seam. */
  credential: string;
  subject: string;
  /** Renews the access token under authit. Absent in the other two modes,
   * where renewal is somebody else's job: oidc-client-ts silently renews a
   * Zitadel token, and a typed subject never expires. */
  refreshToken?: string;
  /** When the access token expires, ISO-8601. Absent for the same reason. */
  expiresAt?: string;
}

/** A child's sign-in: an opaque token with an expiry. */
interface ChildSession {
  token: string;
  childId: string;
  name: string;
  mode: string;
  expiresAt: string;
}

export type Who =
  | { kind: 'nobody' }
  | { kind: 'child'; childId: string; name: string; mode: string }
  | { kind: 'parent'; subject: string };

interface SessionState {
  pairing: Pairing | null;
  who: Who;
  /** True once a parent has entered parent mode with the PIN. */
  parentMode: boolean;
  /** True when a parent credential is on the device at all. */
  hasParent: boolean;
  /** What the transport is currently sending, for the raw byte routes. */
  credential: Credential | null;
  /** True when a parent is signed in but belongs to no family yet. The
   * device cannot be paired until they found one. */
  needsFamily: boolean;
  /** Sign a parent in with an address and a password (authit). */
  signInPassword: (email: string, password: string) => Promise<void>;
  signInParent: (credential: string, subject: string) => Promise<void>;
  /** Found the family this parent will own, and pair the device to it. */
  createFamily: (name: string, displayName: string) => Promise<void>;
  signInChild: (childId: string, pin: string) => Promise<void>;
  /** Enter parent mode. The PIN was already checked against the server. */
  enterParentMode: () => void;
  /** Back to the profile picker: drop whoever is signed in, keep the pairing. */
  leave: () => void;
  /** Forget the family entirely, and end the session on the server. */
  unpair: () => void;
  /** Re-read the roster, after a child is added, renamed or removed. */
  refreshChildren: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

// Two stores, on purpose.
//
// The **pairing** is in localStorage: it survives the tab, the reboot and the
// week, because the whole point is that the tablet already knows the family.
// It is not a credential — a family id and some first names — and the thing it
// unlocks is a PIN screen the server checks.
//
// A **credential** is in sessionStorage, for the reason coachai's session.ts
// gives: a token that outlives the tab is one still sitting on a machine after
// the person walked away, and this machine is in a hallway. The parent's is
// therefore per-tab, and the child's goes with it — six hours is its own
// limit, and a closed tab is a child who has finished.
function store(key: string): Storage {
  return key === PAIRING_KEY ? localStorage : sessionStorage;
}

function read<T>(key: string): T | null {
  try {
    const raw = store(key).getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown | null): void {
  try {
    if (value === null) store(key).removeItem(key);
    else store(key).setItem(key, JSON.stringify(value));
  } catch {
    // a browser with storage disabled still works; it just forgets.
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [pairing, setPairing] = useState<Pairing | null>(() => read<Pairing>(PAIRING_KEY));
  const [parent, setParent] = useState<Parent | null>(() => read<Parent>(PARENT_KEY));
  const [child, setChild] = useState<ChildSession | null>(() => {
    const c = read<ChildSession>(CHILD_KEY);
    // A token that expired while the tablet was asleep is not a session.
    if (c && Date.parse(c.expiresAt) < Date.now()) {
      write(CHILD_KEY, null);
      return null;
    }
    return c;
  });
  const [parentMode, setParentMode] = useState(false);
  const [needsFamily, setNeedsFamily] = useState(false);

  // The credential the transport should use. A signed-in child wins: while a
  // child is at the tablet, nothing acts as the parent, even though the
  // parent's token is still on the device.
  const credential = useMemo<Credential | null>(() => {
    if (child) return { kind: 'child', value: child.token };
    if (parent) return { kind: 'parent', value: parent.credential };
    return null;
  }, [child, parent]);

  const leave = useCallback(() => {
    write(CHILD_KEY, null);
    setChild(null);
    setParentMode(false);
  }, []);

  // Configure once, with a function that reads the current credential — see
  // api/client.ts on why this is not a captured value.
  const [configured, setConfigured] = useState(false);
  const credentialRef = useRef(credential);
  credentialRef.current = credential;
  useEffect(() => {
    if (configured) return;
    configureClient(
      () => credentialRef.current,
      () => {
        // A 401 anywhere drops to the profile picker. It is the right landing
        // for the common case — a six-hour token that ran out mid-afternoon —
        // and harmless for the rare one.
        write(CHILD_KEY, null);
        setChild(null);
        setParentMode(false);
      },
    );
    setConfigured(true);
  }, [configured, credentialRef]);

  const loadRoster = useCallback(async (orgId: string, orgName: string) => {
    const body = await unwrap(listChildren({ headers: { 'X-Org-ID': orgId } }));
    const next: Pairing = { orgId, orgName, children: body?.children ?? [] };
    write(PAIRING_KEY, next);
    setPairing(next);
  }, []);

  const signInParent = useCallback(
    async (cred: string, subject: string, tokens?: { refreshToken: string; expiresAt: string }) => {
      const next: Parent = { credential: cred, subject, ...tokens };
      write(PARENT_KEY, next);
      setParent(next);
      // Read the memberships with the credential just given rather than the one
      // in state, which React has not committed yet.
      const auth =
        AUTH_MODE === 'header'
          ? { 'X-Test-Subject': cred }
          : { Authorization: `Bearer ${cred}` };
      const body = await unwrap(me({ headers: auth }));
      const first: Membership | undefined = body?.memberships?.[0];
      if (!first) {
        // A parent who has just confirmed their address belongs to no family
        // yet. That is a step, not a failure: the next screen founds one.
        setNeedsFamily(true);
        return;
      }
      setNeedsFamily(false);
      await loadRoster(first.orgId, first.orgName);
      setParentMode(true);
    },
    [loadRoster],
  );

  const signInPassword = useCallback(
    async (email: string, password: string) => {
      const pair = await unwrap(login({ body: { email, password } }));
      if (!pair) throw new Error('Anmeldung fehlgeschlagen.');
      // The subject is whatever the server says it is; the token is opaque
      // here and is never decoded in the browser.
      await signInParent(pair.accessToken, email, {
        refreshToken: pair.refreshToken,
        expiresAt: pair.expiresAt,
      });
    },
    [signInParent],
  );

  const createFamily = useCallback(
    async (name: string, displayName: string) => {
      const founded = await unwrap(
        createOrganization({ body: { name, slug: slugOf(name), displayName } }),
      );
      if (!founded) throw new Error('Die Familie konnte nicht angelegt werden.');
      // A family that arrived through an invitation says so now, while it is
      // new enough to count. A refusal — a stale or mistyped code — costs it
      // the longer trial and nothing else, so it does not stop the founding.
      const code = pendingReferral();
      if (code) {
        await claimReferral({ headers: { 'X-Org-ID': founded.organization.id }, body: { code } });
        clearReferral();
      }
      setNeedsFamily(false);
      await loadRoster(founded.organization.id, founded.organization.name);
      setParentMode(true);
    },
    [loadRoster],
  );

  const signInChild = useCallback(
    async (childId: string, pin: string) => {
      if (!pairing) throw new Error('Dieses Gerät kennt noch keine Familie.');
      const session = await unwrap(
        childSignIn({ body: { orgId: pairing.orgId, childId, pin } }),
      );
      if (!session) throw new Error('Anmeldung fehlgeschlagen.');
      const next: ChildSession = {
        token: session.token,
        childId: session.childId,
        name: session.name,
        mode: session.mode,
        expiresAt: session.expiresAt,
      };
      write(CHILD_KEY, next);
      setChild(next);
      setParentMode(false);
    },
    [pairing],
  );

  const unpair = useCallback(() => {
    // Under authit the refresh token is a row on the server, so forgetting it
    // here would leave a session nobody can end. Best effort: a device that
    // is offline still forgets locally, and the token expires on its own.
    if (parent?.refreshToken) {
      void logout({ body: { refreshToken: parent.refreshToken } }).catch(() => {});
    }
    write(CHILD_KEY, null);
    write(PARENT_KEY, null);
    write(PAIRING_KEY, null);
    setChild(null);
    setParent(null);
    setPairing(null);
    setParentMode(false);
    setNeedsFamily(false);
  }, [parent]);

  const refreshChildren = useCallback(async () => {
    if (!pairing) return;
    await loadRoster(pairing.orgId, pairing.orgName);
  }, [pairing, loadRoster]);

  // The access token authit mints is short-lived, so it is renewed a minute
  // before it runs out rather than after a request has already failed. The
  // other two modes carry no refresh token: oidc-client-ts renews a Zitadel
  // token itself, and a typed subject never expires.
  useEffect(() => {
    if (!parent?.refreshToken || !parent.expiresAt) return;
    const due = Date.parse(parent.expiresAt) - 60_000 - Date.now();
    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            const pair = await unwrap(
              refreshSession({ body: { refreshToken: parent.refreshToken as string } }),
            );
            if (!pair) return;
            const next: Parent = {
              ...parent,
              credential: pair.accessToken,
              refreshToken: pair.refreshToken,
              expiresAt: pair.expiresAt,
            };
            write(PARENT_KEY, next);
            setParent(next);
          } catch {
            // The session is over. The 401 handler takes it from here the
            // next time anything asks the server for something.
          }
        })();
      },
      Math.max(due, 0),
    );
    return () => clearTimeout(timer);
  }, [parent]);

  const who: Who = child
    ? { kind: 'child', childId: child.childId, name: child.name, mode: child.mode }
    : parentMode && parent
      ? { kind: 'parent', subject: parent.subject }
      : { kind: 'nobody' };

  const value = useMemo<SessionState>(
    () => ({
      pairing,
      who,
      parentMode,
      hasParent: parent !== null,
      needsFamily,
      credential,
      signInPassword,
      signInParent,
      createFamily,
      signInChild,
      enterParentMode: () => setParentMode(true),
      leave,
      unpair,
      refreshChildren,
    }),
    [
      pairing,
      who,
      parentMode,
      parent,
      needsFamily,
      credential,
      signInPassword,
      signInParent,
      createFamily,
      signInChild,
      leave,
      unpair,
      refreshChildren,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The family every request is for. Throws where there is none, which is a bug
 * rather than a state: nothing renders before the device is paired. */
export function useOrg(): { 'X-Org-ID': string } {
  const { pairing } = useSession();
  if (!pairing) throw new Error('no family on this device');
  return { 'X-Org-ID': pairing.orgId };
}

/**
 * A URL-safe slug from a family's name, because the API asks for one and a
 * parent should not have to think about it. Diacritics are folded rather
 * than dropped, so "Familie Müller" is `familie-mueller` and not
 * `familie-mller`; a name with nothing left after folding falls back to
 * something unique rather than empty.
 */
function slugOf(name: string): string {
  const folded = name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const slug = folded.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug.length >= 2 ? slug.slice(0, 64) : `familie-${Date.now().toString(36)}`;
}

/** The credential, for the raw byte routes. */
export function useCredential(): Credential | null {
  return useSession().credential;
}
